import { useState } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, Modal, Select, Space, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import useLocale from '../../../hooks/useLocale';
import type { ClientService } from '../../../shared/api/ClientService';
import StyledTable from '../components/StyledTable/StyledTable';
import ActionResultModal from './ActionResultModal';
import CommandActionButton from './CommandActionButton';
import CommandDetailModal from './CommandDetailModal';
import LoadErrorAlert from './LoadErrorAlert';
import { getCommandColumns } from './commandColumns';
import {
  COMMAND_COLUMN_WIDTH,
  COMMAND_EMPTY_TEXT_DRAFTS,
  COMMAND_FILTER_IDS,
  COMMAND_STATUS_FILTER_ORDER,
  COMMAND_TABLE_SCROLL_X,
  DEFAULT_COMMAND_FILTERS,
  DEFAULT_COMMAND_SORT,
} from './constants';
import type { CommandAction, CommandStatus, IActionOutcome, ICommandFilters, ICommandSort, ITextDrafts } from './types';
import { useCancelCommands, useIntegrationCommands, useRetryCommands } from './useIntegrationMonitoring';
import { useLeaveEmptyPage, useListControls } from './useListControls';
import {
  buildListParams,
  formatText,
  getErrorMessage,
  getIds,
  getStatusLabel,
  isSelectable,
  summariseActionResult,
  toDateRange,
  toSortState,
} from './utils';

type Command = ClientService.IntegrationCommandDto;

const NO_COMMANDS: Command[] = [];

// StyledTable is not generic, so the row type is kept on these helpers.
const getRowKey = ({ id }: Command): string => id ?? '';
const getCheckboxProps = ({ status }: Command) => ({ disabled: !isSelectable(status) });

const CommandsTab = (): JSX.Element => {
  const { t } = useLocale();
  const controls = useListControls<ICommandFilters, ICommandSort>({
    defaultFilters: DEFAULT_COMMAND_FILTERS,
    defaultSort: DEFAULT_COMMAND_SORT,
    toSort: toSortState,
  });
  const { filters, sort, page, pageSize, selectedIds, setSelectedIds, changeFilters } = controls;
  const [textDrafts, setTextDrafts] = useState<ITextDrafts>(COMMAND_EMPTY_TEXT_DRAFTS);
  const [detailId, setDetailId] = useState<string>();
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [outcome, setOutcome] = useState<IActionOutcome>();

  const retryMutation = useRetryCommands();
  const cancelMutation = useCancelCommands();
  const commandsQuery = useIntegrationCommands(buildListParams(filters, sort, page, pageSize));

  const commands = commandsQuery.data?.items ?? NO_COMMANDS;
  const totalCount = commandsQuery.data?.totalCount ?? 0;
  // Only rows that can still take the action count: a row a refresh made unavailable must not stay checked.
  const selectedCommands = commands.filter(
    ({ id, status }) => id !== undefined && selectedIds.includes(id) && isSelectable(status)
  );
  const isPastLastPage =
    commandsQuery.data !== undefined && !commandsQuery.isPreviousData && commands.length === 0 && page > 1;

  useLeaveEmptyPage(isPastLastPage, totalCount, pageSize, controls.setPage);

  const applyTextFilters = ({ commandType, fileName }: ITextDrafts) => {
    const next = { commandType: commandType.trim(), fileName: fileName.trim() };
    if (next.commandType !== filters.commandType || next.fileName !== filters.fileName) changeFilters(next);
  };

  const changeDraft = (field: keyof ITextDrafts, value: string) => {
    const drafts: ITextDrafts = { ...textDrafts, [field]: value };
    setTextDrafts(drafts);
    // Clearing a box has nothing to wait for.
    if (!value) applyTextFilters(drafts);
  };

  const resetFilters = () => {
    controls.resetFilters();
    setTextDrafts(COMMAND_EMPTY_TEXT_DRAFTS);
  };

  const openDetail = (commandId?: string) => {
    if (!commandId) return;
    setDetailId(commandId);
    setIsDetailOpen(true);
  };

  const runAction = async (action: CommandAction, targets: Command[]) => {
    const isCancel = action === 'cancel';
    try {
      const result = await (isCancel ? cancelMutation : retryMutation).mutateAsync(getIds(targets));
      // The call returned, so these rows are dealt with whatever the outcome of each one.
      setSelectedIds([]);
      const summary = summariseActionResult(result, targets);
      if (summary.failures.length === 0) {
        message.success(
          formatText(isCancel ? t.INTEGRATION_CANCEL_DONE : t.INTEGRATION_RETRY_DONE, summary.succeededCount)
        );
      } else {
        setOutcome({ title: isCancel ? t.INTEGRATION_CANCEL_RESULT_TITLE : t.INTEGRATION_RETRY_RESULT_TITLE, summary });
      }
    } catch (error) {
      // The call itself failed: the selection stays, so the operator does not select up to 100 rows again.
      message.error(getErrorMessage(error, t.INTEGRATION_ACTION_FAILED));
    }
  };

  const confirmAction = (action: CommandAction, targets: Command[]) => {
    const isCancel = action === 'cancel';
    Modal.confirm({
      title: formatText(
        isCancel ? t.INTEGRATION_CANCEL_CONFIRM_TITLE : t.INTEGRATION_RETRY_CONFIRM_TITLE,
        targets.length
      ),
      icon: <ExclamationCircleOutlined />,
      content: isCancel ? t.INTEGRATION_CANCEL_CONFIRM_BODY : t.INTEGRATION_RETRY_CONFIRM_BODY,
      okText: isCancel ? t.INTEGRATION_CANCEL_COMMANDS : t.RETRY,
      okType: isCancel ? 'danger' : 'primary',
      cancelText: isCancel ? t.INTEGRATION_KEEP_COMMANDS : t.CANCEL,
      // Enter must not cancel by accident; the Retry confirmation keeps the default focus on its OK button.
      autoFocusButton: isCancel ? 'cancel' : undefined,
      onOk: () => runAction(action, targets),
    });
  };

  const statusOptions = COMMAND_STATUS_FILTER_ORDER.map((status) => ({
    value: status,
    label: getStatusLabel(status, t),
  }));
  const selectedStatuses = selectedCommands.map(({ status }) => status);

  return (
    <>
      <Form layout="inline" className="integration-monitoring__filters">
        <Form.Item label={t.STATUS} htmlFor={COMMAND_FILTER_IDS.status}>
          <Select<CommandStatus>
            allowClear
            id={COMMAND_FILTER_IDS.status}
            className="integration-monitoring__filter-control"
            placeholder={t.ALL}
            options={statusOptions}
            value={filters.status}
            onChange={(status) => changeFilters({ status })}
          />
        </Form.Item>
        <Form.Item label={t.INTEGRATION_COMMAND_TYPE} htmlFor={COMMAND_FILTER_IDS.commandType}>
          <Input
            allowClear
            id={COMMAND_FILTER_IDS.commandType}
            className="integration-monitoring__filter-control"
            placeholder={t.INTEGRATION_COMMAND_TYPE_EXACT}
            value={textDrafts.commandType}
            onChange={({ target }) => changeDraft('commandType', target.value)}
            onPressEnter={() => applyTextFilters(textDrafts)}
            onBlur={() => applyTextFilters(textDrafts)}
          />
        </Form.Item>
        <Form.Item label={t.INTEGRATION_FILE_NAME} htmlFor={COMMAND_FILTER_IDS.fileName}>
          <Input.Search
            allowClear
            id={COMMAND_FILTER_IDS.fileName}
            className="integration-monitoring__filter-control"
            placeholder={t.INTEGRATION_FILE_NAME_START}
            value={textDrafts.fileName}
            onChange={({ target }) => changeDraft('fileName', target.value)}
            // antd calls onSearch before onChange when the x clears the box, so use the value it passes.
            onSearch={(fileName) => applyTextFilters({ ...textDrafts, fileName })}
            onBlur={() => applyTextFilters(textDrafts)}
          />
        </Form.Item>
        <Form.Item label={t.CREATED} htmlFor={COMMAND_FILTER_IDS.created}>
          <DatePicker.RangePicker
            id={COMMAND_FILTER_IDS.created}
            value={filters.createdRange}
            onChange={(range) => changeFilters({ createdRange: toDateRange(range) })}
          />
        </Form.Item>
        <Form.Item>
          <Checkbox checked={filters.heldOnly} onChange={(event) => changeFilters({ heldOnly: event.target.checked })}>
            {t.INTEGRATION_HELD_ONLY}
          </Checkbox>
        </Form.Item>
        <Form.Item>
          <Button onClick={resetFilters}>{t.RESET}</Button>
        </Form.Item>
      </Form>

      <Space className="integration-monitoring__toolbar" wrap>
        <CommandActionButton
          action="retry"
          statuses={selectedStatuses}
          label={formatText(t.INTEGRATION_RETRY_SELECTED, selectedCommands.length)}
          onClick={() => confirmAction('retry', selectedCommands)}
        />
        <CommandActionButton
          action="cancel"
          statuses={selectedStatuses}
          label={formatText(t.INTEGRATION_CANCEL_SELECTED, selectedCommands.length)}
          onClick={() => confirmAction('cancel', selectedCommands)}
        />
      </Space>

      {commandsQuery.isError && <LoadErrorAlert error={commandsQuery.error} onRetry={() => commandsQuery.refetch()} />}

      <StyledTable
        rowKey={getRowKey}
        columns={getCommandColumns({ t, sort, onView: openDetail })}
        dataSource={commands}
        loading={commandsQuery.isFetching}
        scroll={{ x: COMMAND_TABLE_SCROLL_X }}
        onChange={controls.handleTableChange}
        rowSelection={{
          selectedRowKeys: getIds(selectedCommands),
          columnWidth: COMMAND_COLUMN_WIDTH.selection,
          onChange: (keys) => setSelectedIds(keys.map(String)),
          getCheckboxProps,
        }}
        pagination={controls.getPagination(totalCount)}
      />

      <CommandDetailModal
        commandId={detailId}
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onAction={confirmAction}
      />
      {outcome && <ActionResultModal outcome={outcome} onClose={() => setOutcome(undefined)} />}
    </>
  );
};

export default CommandsTab;
